import { OfferFragment, EarningMechanismConfigurationInput, OfferCreateInput } from '@occc/graphql';

import {
  ConstraintDuplicationService,
  constraintDuplicationService,
} from './constraintDuplicationService';
import {
  DisplayCategoriesDuplicationService,
  displayCategoriesDuplicationService,
} from './displayCategoriesDuplicationService';
import {
  EarningMechanismDuplicationService,
  earningMechanismDuplicationService,
} from './earningMechanismDuplicationService';
import { LimitDuplicationService, limitDuplicationService } from './limitDuplicationService';
import {
  MarketingContentDuplicationService,
  marketingContentDuplicationService,
} from './marketingContentDuplicationService';

export class OfferDuplicationService {
  private earningMechanismDuplicationService: EarningMechanismDuplicationService;
  private limitDuplicationService: LimitDuplicationService;
  private constraintDuplicationService: ConstraintDuplicationService;
  private marketingContentDuplicationService: MarketingContentDuplicationService;
  private displayCategoriesDuplicationService: DisplayCategoriesDuplicationService;

  constructor(
    earningMechanismDuplicationService: EarningMechanismDuplicationService,
    limitDuplicationService: LimitDuplicationService,
    constraintDuplicationService: ConstraintDuplicationService,
    marketingContentDuplicationService: MarketingContentDuplicationService,
    displayCategoriesDuplicationService: DisplayCategoriesDuplicationService,
  ) {
    this.earningMechanismDuplicationService = earningMechanismDuplicationService;
    this.limitDuplicationService = limitDuplicationService;
    this.constraintDuplicationService = constraintDuplicationService;
    this.marketingContentDuplicationService = marketingContentDuplicationService;
    this.displayCategoriesDuplicationService = displayCategoriesDuplicationService;
  }

  public copy(
    offer: OfferFragment & { earningMechanism: OfferFragment['earningMechanism'] },
    chainId: string,
    campaignUuid?: string,
  ) {
    const offerCreateInput = this.getOfferCreateInput(offer, chainId, campaignUuid);

    const earningMechanismInput: EarningMechanismConfigurationInput | null = offer.earningMechanism
      ? this.earningMechanismDuplicationService.copy(offer.earningMechanism)
      : null;

    const getMarketingContentsInput = (newOfferId: string) => {
      return this.marketingContentDuplicationService.copy(offer.marketingContents, newOfferId);
    };

    const getDisplayCategoriesInput = (newOfferId: string) => {
      return this.displayCategoriesDuplicationService.copy(offer.displayCategories, newOfferId);
    };

    const limitInputs = this.limitDuplicationService.copy(offer.limits);
    const constraintInputs = this.constraintDuplicationService.copy(offer.constraints);

    return {
      offerCreateInput,
      earningMechanismInput,
      getMarketingContentsInput,
      getDisplayCategoriesInput,
      limitInputs,
      constraintInputs,
    };
  }

  private getOfferCreateInput(
    offer: OfferFragment,
    chainId: string,
    campaignUuid?: string,
  ): OfferCreateInput {
    const {
      name,
      description,
      presentationPriority,
      presentationType,
      category,
      triggerPreferences: { triggerOnFulfillment, triggerOnProgress, triggerOnVisibility },
      ageRestricted,
      customData,
      externalId,
      fundingSource,
      stackable,
      shortDescription,
      longDescription,
      optionalAtPos,
      fundingSourceName,
      fulfillmentPriorityLevel,
      visibleForCommandCenterFulfillment,
      displayForOrderAhead,
      trackingConfig,
    } = offer;

    const offerCreateInput = {
      engagementType: offer.engagementType!,
      chainId,
      name: `copy: ${name}`,
      description,
      presentationPriority,
      presentationType,
      campaignUuid,
      category,
      triggerPreferences: {
        triggerOnFulfillment,
        triggerOnProgress,
        triggerOnVisibility,
      },
      // https://stuzo.atlassian.net/browse/OCST-3238
      // to prevent scenarios where an active offer is copied and applied immediately
      publishAt: null, // explicitly set to null
      expireAt: null, // explicitly set to null
      ageRestricted,
      customData,
      externalId,
      fundingSource,
      stackable,
      shortDescription,
      longDescription,
      optionalAtPos,
      fundingSourceName,
      fulfillmentPriorityLevel,
      visibleForCommandCenterFulfillment,
      displayForOrderAhead,
      trackingConfig: trackingConfig
        ? {
            trackingId: trackingConfig.trackingId,
            isActivityLogIncludedInAltriaDDM: trackingConfig.isActivityLogIncludedInAltriaDDM,
          }
        : undefined,
      scanDataConfiguration: offer.scanDataConfiguration
        ? {
            description: offer.scanDataConfiguration.description,
            discountAllocation: offer.scanDataConfiguration.discountAllocation,
            fundingSource: offer.scanDataConfiguration.fundingSource,
          }
        : undefined,
    };

    // The next section is used to ensure that every field in OfferCreateInput is handled
    // in the offerCreateInput object. If not, TypeScript will raise a compile-time error.

    type HandledOfferCreateKeys = keyof typeof offerCreateInput;
    type AllOfferCreateKeys = keyof OfferCreateInput;
    type UnhandledOfferCreateKeys = Exclude<AllOfferCreateKeys, HandledOfferCreateKeys>;

    // If a new key is added to OfferCreateInput and not handled in the code, TypeScript will flag it as an error.
    // You can try to remove fields from the offerCreateInput object to see the error.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const checkMissingOfferCreateFields: {
      [key in UnhandledOfferCreateKeys]: never;
    } = {};

    return offerCreateInput;
  }
}

export const offerDuplicationService = new OfferDuplicationService(
  earningMechanismDuplicationService,
  limitDuplicationService,
  constraintDuplicationService,
  marketingContentDuplicationService,
  displayCategoriesDuplicationService,
);
